import React, { useRef, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Box, Button, LinearProgress } from '@mui/material';
import validateImage from '../../utils/Image-validation';
// import { uploadSeets } from '../../services/import-export.service';
import {
  completeVideoUpload,
  getMultipartSignedUrl,
  getSignedUrlOfCDN77,
  getVideoUploadURL,
  uploadFileToS3,
} from '../../services/videos.service';

const Upload = ({
  videoId,
  title,
  updateImage,
  sourceType,
  s3Folder,
  aspectRatio,
  // allowedSize,
  setSnackbarInfo,
  type,
  details,
  from,
  isMultipleSubtitle,
  subtitleLabel,
  isSeries,
}) => {
  const hiddenFileInput = useRef(null);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [buttonTitle, setButtonTitle] = useState(title);
  const [uploadProgress, setUploadProgress] = useState(0);

  let allowedTypes = '.jpg,.jpeg,';
  let srtFileExtension = '';
  let successMessage = 'Image uploaded successfully.';

  if (type === 'subtitle') {
    allowedTypes = '.srt';
    successMessage = 'SRT file uploaded successfully.';
  } else if (type === 'video' && sourceType === 'video') {
    allowedTypes = 'video/*';
    successMessage =
      'Video uploaded successfully. Encoding will begin shortly.';
  } else if (type === 'video' && sourceType === 'trailer') {
    allowedTypes = 'video/*';
    successMessage =
      'Trailer video uploaded successfully. Encoding will begin shortly.';
  }

  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  const uploadChunk = async (selectedFile, params, uploadId) => {
    const FILE_CHUNK_SIZE = 1024 * 1024 * 50; // 50MB
    const fileSize = selectedFile.size;
    const NUM_CHUNKS = Math.floor(fileSize / FILE_CHUNK_SIZE) + 1;
    const promisesArray = [];
    let completedBytes = 0;

    for (let index = 1; index <= NUM_CHUNKS; index += 1) {
      const start = (index - 1) * FILE_CHUNK_SIZE;
      const end = index * FILE_CHUNK_SIZE;
      const blob =
        index < NUM_CHUNKS
          ? selectedFile.slice(start, end)
          : selectedFile.slice(start);

      // Copy completedBytes for this chunk’s scope
      const baseBytes = completedBytes;

      // (1) Generate presigned URL for each part
      const getUploadUrlResp = await getVideoUploadURL({
        fileName: params.fileName,
        partNumber: index,
        path: params.folder,
        uploadId,
      });

      const { presignedUrl } = getUploadUrlResp.data;

      // (2) Puts each file part into the storage server
      const uploadResp = await axios.put(presignedUrl, blob, {
        headers: { 'Content-Type': params.fileType },
        onUploadProgress: (evt) => {
          const uploadedBytes = evt.loaded + baseBytes;
          const progress = Math.round(
            (uploadedBytes * 100) / selectedFile.size
          );
          setUploadProgress(progress);
        },
      });

      // Update completedBytes after finishing this chunk
      completedBytes += blob.size;

      promisesArray.push(uploadResp);
    }

    const resolvedArray = await Promise.all(promisesArray);

    const uploadPartsArray = resolvedArray.map((resolvedPromise, idx) => ({
      ETag: resolvedPromise.headers.etag,
      PartNumber: idx + 1,
    }));

    // (3) Calls the completeVideoUpload endpoint in the backend server
    await completeVideoUpload({
      fileName: params.fileName,
      parts: uploadPartsArray,
      folder: params.folder,
      isTranscode: params.isTranscode,
      videoType: sourceType,
      videoId,
      uploadId,
    });

    setBtnDisabled(false);
    setSnackbarInfo({
      show: true,
      type: 'success',
      message: successMessage,
    });
    setButtonTitle(title);
    hiddenFileInput.current.value = '';
    setTimeout(() => {
      setUploadProgress(0);
    }, 250);
  };

  const handleFileChange = (event) => {
    setSnackbarInfo({
      show: false,
    });
    setBtnDisabled(true);
    const selectedFile = event.target.files[0];

    if (!selectedFile) {
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Please select file.',
        });
      }, 100);
      return;
    }

    const acceptedImages = ['image/jpeg', 'image/jpg'];

    const isMissingTitle = !details.title && !details.name;
    const isPlaylistOrSeries = from === 'playlist' || isSeries;
    if (isPlaylistOrSeries && isMissingTitle) {
      updateImage('');
      setBtnDisabled(false);
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Please add a title before uploading the poster.',
        });
      }, 100);
      hiddenFileInput.current.value = '';
      return;
    }

    if (type === 'subtitle') {
      if (isMultipleSubtitle && !subtitleLabel) {
        updateImage('');
        setBtnDisabled(false);
        setTimeout(() => {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: 'Please add the appropriate label.',
          });
        }, 100);
        hiddenFileInput.current.value = '';
        return;
      }
      acceptedImages.push('application/x-subrip');
      const parts = selectedFile.name.split('.');
      const extension = parts.length > 1 ? parts.pop().toLowerCase() : '';
      srtFileExtension = extension;
    }

    if (!['srt', '.srt'].includes(srtFileExtension) && type === 'subtitle') {
      updateImage('');
      setBtnDisabled(false);
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Please select only srt file.',
        });
      }, 100);
      hiddenFileInput.current.value = '';
      return;
    }

    if (!acceptedImages.includes(selectedFile.type) && !type === 'subtitle') {
      updateImage('');
      setBtnDisabled(false);
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Please select an image of type jpeg, jpg',
        });
      }, 100);
      hiddenFileInput.current.value = '';
      return;
    }

    if (type === 'video') {
      // upload video
      const notAllowed = ['video/quicktime'];
      let fileExtestion = '';
      if (selectedFile?.type) {
        fileExtestion = selectedFile.type;
      } else {
        const parts = selectedFile.name.split('.');
        const extension = parts.length > 1 ? parts.pop().toLowerCase() : '';
        if (!selectedFile.type && extension === 'mp4') {
          fileExtestion = 'video/mp4';
        }
      }

      if (fileExtestion !== 'video/mp4' || notAllowed.includes(fileExtestion)) {
        setBtnDisabled(false);
        setTimeout(() => {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: 'Please select proper mp4 video file.',
          });
        }, 100);
        hiddenFileInput.current.value = '';
        return;
      }
      const params = {
        fileName: selectedFile.name,
        fileType: fileExtestion,
        folder: s3Folder,
        isTranscode: true,
      };
      getMultipartSignedUrl(params)
        .then((res) => {
          // handle response, expect uploadId
          const uploadId = res?.data?.uploadId;
          uploadChunk(selectedFile, params, uploadId);
        })
        .catch((err) => {
          setButtonTitle(title);
          setBtnDisabled(false);
          const message =
            err?.message || 'Something went wrong, please try again.';
          setSnackbarInfo({
            show: true,
            type: 'error',
            message,
          });
          hiddenFileInput.current.value = '';
        });
    } else {
      validateImage(
        selectedFile,
        aspectRatio,
        null,
        false,
        false,
        false,
        false,
        type
      )
        .then(async () => {
          setButtonTitle('Uploading...');
          const isResize = type === 'poster';
          const seriesTitle = details?.seriesTitle || '';
          const videoTitle = details?.title || details?.name || '';
          const year = details?.year || '';
          const season = details?.season || '';
          const episodeNumber = details?.episodeNumber || '';
          const seriesId = details?.seriesId || '';

          const signedUrlData = await getSignedUrlOfCDN77({
            fileName: selectedFile.name.toLowerCase(),
            fileType: selectedFile.type,
            folder: s3Folder,
            isCreateSamllImage: isResize,
            seriesTitle,
            title: videoTitle,
            year,
            season,
            episodeNumber,
            seriesId,
            from,
            sourceType,
            subtitleLabel,
          });

          if (!signedUrlData) {
            return;
          }

          try {
            await uploadFileToS3(
              selectedFile,
              signedUrlData.data.uploadUrl,
              (progressEvent) => {
                const progress = Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100
                );
                setUploadProgress(progress);
              }
            );
            updateImage(signedUrlData.data.fileUrl);
            setBtnDisabled(false);
            setSnackbarInfo({
              show: true,
              type: 'success',
              message: successMessage,
            });
            setButtonTitle(title);
            hiddenFileInput.current.value = '';
            setTimeout(() => {
              setUploadProgress(0);
            }, 250);
          } catch (error) {
            updateImage('');
            setBtnDisabled(false);
            const message =
              error.response?.data?.message ||
              'Something went wrong, please try again.';
            setSnackbarInfo({
              show: true,
              type: 'error',
              message,
            });
            hiddenFileInput.current.value = '';
            setUploadProgress(0);
          }
        })
        .catch((err) => {
          setButtonTitle(title);
          updateImage('');
          setBtnDisabled(false);
          const message =
            err?.message || 'Something went wrong, please try again.';
          setSnackbarInfo({
            show: true,
            type: 'error',
            message,
          });
          hiddenFileInput.current.value = '';
        });
    }
  };

  return (
    <>
      <input
        type="file"
        accept={allowedTypes}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box
        spacing={1}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Box
          item
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
          }}
        >
          <Button
            onClick={handleClick}
            variant="outlined"
            size="medium"
            disabled={btnDisabled}
          >
            {buttonTitle || 'Select File'}
          </Button>
        </Box>

        {uploadProgress > 0 && uploadProgress <= 100 && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '150px',
              gap: 10,
            }}
          >
            <LinearProgress
              style={{ width: '100%' }}
              variant="determinate"
              value={uploadProgress}
            />
            <p>{uploadProgress}%</p>
          </Box>
        )}
      </Box>
    </>
  );
};

Upload.propTypes = {
  videoId: PropTypes.number,
  title: PropTypes.string.isRequired,
  updateImage: PropTypes.func,
  sourceType: PropTypes.string,
  s3Folder: PropTypes.string.isRequired,
  aspectRatio: PropTypes.number.isRequired,
  // allowedSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSnackbarInfo: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  details: PropTypes.objectOf,
  from: PropTypes.string,
  isMultipleSubtitle: PropTypes.bool,
  isSeries: PropTypes.bool,
  subtitleLabel: PropTypes.string,
};

Upload.defaultProps = {
  videoId: 0,
  sourceType: 'null',
  updateImage: () => {},
  details: {},
  from: '',
  isMultipleSubtitle: false,
  isSeries: false,
  subtitleLabel: '',
};

export default Upload;
